import { useFieldArray, useFormContext } from 'react-hook-form';
import { Button, Stack, Typography, IconButton, Box } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import PlusIcon from 'components/v2/icons/PlusIcon';
import { LAYOUT_SPACING } from 'constants/ui/style';
import { useLocalization } from 'localization';
import ContactPerson, { ContactPersonProps } from './ContactPerson';

const MAX_CONTACT_PERSONS = 3; // Maximum number of contact persons allowed

interface SortableContactPersonProps extends Omit<ContactPersonProps, 'index'> {
  fieldIndex: number;
  id: string;
}

const CreateNewOrderContactPerson = () => {
  const { t } = useLocalization();
  const { control, watch } = useFormContext();

  const { fields, append, remove, move } = useFieldArray({
    control: control,
    name: 'contactPersons',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex(field => field.id === active.id);
      const newIndex = fields.findIndex(field => field.id === over?.id);

      move(oldIndex, newIndex);
    }
  };

  return (
    <Stack gap={LAYOUT_SPACING.FORM.xs}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map(field => field.id)} strategy={verticalListSortingStrategy}>
          {fields.map((field, index) => (
            <SortableContactPersonItem
              key={field.id}
              id={field.id}
              fieldIndex={index}
              control={control}
              removeField={remove}
              watch={watch}
              disabled={fields.length === 1}
            />
          ))}
        </SortableContext>
      </DndContext>

      {fields.length < MAX_CONTACT_PERSONS && (
        <Button
          variant="outlined"
          size="large"
          sx={{ width: 'fit-content' }}
          startIcon={<PlusIcon />}
          onClick={() => append({})}
        >
          {t('orders.pages.new.stepThree.addExtraContactAction')}
        </Button>
      )}
    </Stack>
  );
};

const SortableContactPersonItem = ({
  id,
  fieldIndex,
  control,
  watch,
  removeField,
  disabled,
}: SortableContactPersonProps & { disabled?: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      alignItems="flex-start"
      direction="row"
      gap={1}
      sx={{
        border: isDragging ? theme => `2px dashed ${theme.palette.divider}` : 'none',
        borderRadius: 1,
        padding: isDragging ? 1 : 0,
      }}
    >
      {/* Drag Handle */}
      {!disabled && (
        <IconButton
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            marginTop: '-0.2rem',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
          size="small"
        >
          <DragIndicatorIcon />
        </IconButton>
      )}

      {/* Contact Person Component */}
      <Box sx={{ flex: 1 }}>
        <ContactPerson index={fieldIndex} watch={watch} removeField={removeField} control={control} />
      </Box>
    </Stack>
  );
};

export default CreateNewOrderContactPerson;
